import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLingui } from '@lingui/react/macro';
import { Loader2, Wrench } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { maintenanceApi, type CleanupResponse } from '@/lib/api/services/maintenance.api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TenantPurgeCard } from '@/components/support/TenantPurgeCard';
import { RestoreDefaultRolesCard } from '@/components/support/RestoreDefaultRolesCard';
import { SeedDemoOrgCard } from '@/components/support/SeedDemoOrgCard';
import { toast } from 'sonner';

export const Route = createFileRoute('/(protected)/_layout/support/cleanup')({
  component: SupportCleanupPage,
});

function parseCsv(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function parseIdCsv(value: string): number[] {
  return parseCsv(value)
    .map((entry) => Number(entry))
    .filter((id) => Number.isFinite(id));
}

function toJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function SupportCleanupPage() {
  const { t } = useLingui();
  const { isAuthenticated, isLoading: authLoading, hasRole } = useAuth();

  const isSupportOperator = hasRole('SUPER_ADMIN') || hasRole('SUPPORT');

  const [targetEmails, setTargetEmails] = useState('');
  const [targetUserIds, setTargetUserIds] = useState('');
  const [targetRoleCodes, setTargetRoleCodes] = useState('');
  const [dropGlobalRoleCodes, setDropGlobalRoleCodes] = useState('SUPER_ADMIN');
  const [wildcardAllowedRoleCodes, setWildcardAllowedRoleCodes] = useState('SUPER_ADMIN');

  const [rolesPermissionsResult, setRolesPermissionsResult] = useState<CleanupResponse | null>(null);
  const [expiredRolesResult, setExpiredRolesResult] = useState<CleanupResponse | null>(null);

  const tasksQuery = useQuery({
    queryKey: ['maintenance-cleanup-tasks'],
    queryFn: maintenanceApi.listTasks,
    enabled: isAuthenticated && isSupportOperator,
  });

  const rolesPermissionsPreview = useMutation({
    mutationFn: maintenanceApi.previewRolesPermissions,
    onSuccess: (response) => {
      setRolesPermissionsResult(response);
      toast.success(t`Preview loaded`);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : t`Failed to load preview`;
      toast.error(message);
    },
  });

  const rolesPermissionsApply = useMutation({
    mutationFn: maintenanceApi.applyRolesPermissions,
    onSuccess: (response) => {
      setRolesPermissionsResult(response);
      toast.success(t`Cleanup applied successfully`);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : t`Failed to apply cleanup`;
      toast.error(message);
    },
  });

  const expiredRolesPreview = useMutation({
    mutationFn: maintenanceApi.previewExpiredUserRoles,
    onSuccess: (response) => {
      setExpiredRolesResult(response);
      toast.success(t`Preview loaded`);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : t`Failed to load preview`;
      toast.error(message);
    },
  });

  const expiredRolesApply = useMutation({
    mutationFn: maintenanceApi.applyExpiredUserRoles,
    onSuccess: (response) => {
      setExpiredRolesResult(response);
      toast.success(t`Cleanup applied successfully`);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : t`Failed to apply cleanup`;
      toast.error(message);
    },
  });

  if (authLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (!isSupportOperator) {
    return (
      <Navigate
        to="/unauthorized"
        search={{ reason: 'support_only', from: '/support/cleanup', requiredRole: 'SUPER_ADMIN or SUPPORT' }}
        replace
      />
    );
  }

  const sharedPayload = {
    userEmails: parseCsv(targetEmails),
    userIds: parseIdCsv(targetUserIds),
    roleCodes: parseCsv(targetRoleCodes).map((code) => code.toUpperCase()),
  };

  return (
    <div className="flex flex-col gap-5 p-5">
      <div className="flex items-center gap-3">
        <Wrench className="h-6 w-6 text-(--color-text-primary)" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-(--color-text-primary)">{t`Support Cleanup`}</h1>
          <p className="text-sm text-(--color-text-secondary)">{t`Preview and run maintenance cleanup jobs for roles and permissions.`}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t`Scope`}</CardTitle>
          <CardDescription>{t`Optional filters. Leave empty to evaluate all users and roles.`}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="targetEmails">{t`User emails (comma separated)`}</Label>
            <Input
              id="targetEmails"
              value={targetEmails}
              onChange={(e) => setTargetEmails(e.target.value)}
              placeholder={t`user2@example.com, user@example.com`}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="targetUserIds">{t`User IDs (comma separated)`}</Label>
            <Input
              id="targetUserIds"
              value={targetUserIds}
              onChange={(e) => setTargetUserIds(e.target.value)}
              placeholder={t`10, 42`}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="targetRoleCodes">{t`Role codes (comma separated)`}</Label>
            <Input
              id="targetRoleCodes"
              value={targetRoleCodes}
              onChange={(e) => setTargetRoleCodes(e.target.value)}
              placeholder={t`SUPER_ADMIN, CHEF`}
            />
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="roles-permissions" className="w-full">
        <TabsList>
          <TabsTrigger value="roles-permissions">{t`Roles & Permissions`}</TabsTrigger>
          <TabsTrigger value="default-roles">{t`Default Roles`}</TabsTrigger>
          <TabsTrigger value="expired-user-roles">{t`Expired User Roles`}</TabsTrigger>
          <TabsTrigger value="tenant-data">{t`Purge Tenant Data`}</TabsTrigger>
          <TabsTrigger value="seed-demo-org">{t`Seed Demo Org`}</TabsTrigger>
        </TabsList>

        <TabsContent value="roles-permissions" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t`Roles & Permissions Cleanup`}</CardTitle>
              <CardDescription>{t`Removes stale global assignments and normalizes role permissions.`}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="dropGlobalRoleCodes">{t`Drop global role codes`}</Label>
                  <Input
                    id="dropGlobalRoleCodes"
                    value={dropGlobalRoleCodes}
                    onChange={(e) => setDropGlobalRoleCodes(e.target.value)}
                    placeholder={t`SUPER_ADMIN`}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wildcardAllowedRoleCodes">{t`Wildcard allowed role codes`}</Label>
                  <Input
                    id="wildcardAllowedRoleCodes"
                    value={wildcardAllowedRoleCodes}
                    onChange={(e) => setWildcardAllowedRoleCodes(e.target.value)}
                    placeholder={t`SUPER_ADMIN`}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={rolesPermissionsPreview.isPending}
                  onClick={() =>
                    rolesPermissionsPreview.mutate({
                      ...sharedPayload,
                      dropGlobalRoleCodes: parseCsv(dropGlobalRoleCodes).map((code) => code.toUpperCase()),
                      wildcardAllowedRoleCodes: parseCsv(wildcardAllowedRoleCodes).map((code) => code.toUpperCase()),
                    })
                  }
                >
                  {rolesPermissionsPreview.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t`Preview`}
                </Button>
                <Button
                  disabled={rolesPermissionsApply.isPending}
                  onClick={() =>
                    rolesPermissionsApply.mutate({
                      ...sharedPayload,
                      dropGlobalRoleCodes: parseCsv(dropGlobalRoleCodes).map((code) => code.toUpperCase()),
                      wildcardAllowedRoleCodes: parseCsv(wildcardAllowedRoleCodes).map((code) => code.toUpperCase()),
                      confirm: true,
                    })
                  }
                >
                  {rolesPermissionsApply.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t`Apply`}
                </Button>
              </div>

              {rolesPermissionsResult && (
                <pre className="max-h-120 overflow-auto rounded-md border bg-(--color-surface-muted) p-3 text-xs text-(--color-text-primary)">
                  {toJson(rolesPermissionsResult)}
                </pre>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="default-roles" className="mt-4">
          <RestoreDefaultRolesCard />
        </TabsContent>

        <TabsContent value="expired-user-roles" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t`Expired User Roles Cleanup`}</CardTitle>
              <CardDescription>{t`Deletes expired user role assignments and refreshes authorization policies.`}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={expiredRolesPreview.isPending}
                  onClick={() => expiredRolesPreview.mutate(sharedPayload)}
                >
                  {expiredRolesPreview.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t`Preview`}
                </Button>
                <Button
                  disabled={expiredRolesApply.isPending}
                  onClick={() => expiredRolesApply.mutate({ ...sharedPayload, confirm: true })}
                >
                  {expiredRolesApply.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t`Apply`}
                </Button>
              </div>

              {expiredRolesResult && (
                <pre className="max-h-120 overflow-auto rounded-md border bg-(--color-surface-muted) p-3 text-xs text-(--color-text-primary)">
                  {toJson(expiredRolesResult)}
                </pre>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tenant-data" className="mt-4">
          <TenantPurgeCard />
        </TabsContent>

        <TabsContent value="seed-demo-org" className="mt-4">
          <SeedDemoOrgCard />
        </TabsContent>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle>{t`Registered Tasks`}</CardTitle>
          <CardDescription>{t`Tasks reported by the API maintenance endpoint.`}</CardDescription>
        </CardHeader>
        <CardContent>
          {tasksQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-(--color-text-secondary)">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t`Loading tasks...`}
            </div>
          ) : (
            <pre className="max-h-80 overflow-auto rounded-md border bg-(--color-surface-muted) p-3 text-xs text-(--color-text-primary)">
              {toJson(tasksQuery.data ?? [])}
            </pre>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
